import { FC, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Table, DatePicker, Input, Row, Col, Spin } from 'antd';
import { CloseSvg } from 'utils/svgs';
import { NumericFormat } from 'react-number-format';
import { DownCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import styles from './index.module.scss';
import './index.scss';
import { useSelector, useDispatch } from 'react-redux';
import { toastText, checkPermission } from 'utils/utils';
import {
	getExpenseJournalPreviewEntriesAction,
	getExpenseJournalListAction,
} from 'redux/action/expenseJournalAction';
import { previewOneOffExpenseJournalEntriesAction } from 'redux/action/oneOffAction';
import { AppDispatch } from 'redux/store';
import Ellipse from 'components/Global/Ellipse';
import { getApi, postApi, getApiCSV } from 'redux/apis';
import { EJournalStatus } from 'constants/Data';
import moment from 'moment-timezone';
import { DuplicateJournalErrorModal } from 'components/Global/DuplicateJournalErrorModal';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

const { TextArea } = Input;
const { Column } = Table;
interface Props {
	isOpen: boolean;
	onClose: () => void;
	selectedExpenseGroup: string;
	dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
	expenseGroupOptions?: { value: string; label: string }[];
	existingJournalData?: any;
	isExistingJournal?: boolean;
	journalId?: string; // Add journalId for unique identification
	onSwitchToJournalEntries?: () => void; // Callback to switch to Journal Entries tab
	hideSaveAsDraft?: boolean; // Hide "Save as Draft" button when true
	configWasSaved?: boolean; // Flag indicating if allocation config was saved when user clicked "Yes"
}

interface JournalRow {
	key: string;
	id: number | string;
	accountId: string;
	account: string;
	type: 'Credit' | 'Debit';
	debit?: number;
	credit?: number;
	customerId?: string;
	customer?: string;
	classId?: string;
	class?: string;
	locationId?: string;
	location?: string;
	description?: string;
}

// Custom hook for loading messages (mimicking PreviewJournal)
const useLoadingMessages = () => {
	const [messageIndex, setMessageIndex] = useState(0);

	const loadingMessages = [
		'Fetching GL accounts...',
		'Loading expense transactions...',
		'Processing expense allocations...',
		'Calculating totals...',
		'Creating Journal Entries...',
	];

	useEffect(() => {
		const interval = setInterval(() => {
			setMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length);
		}, 3000);

		return () => clearInterval(interval);
	}, []);

	return loadingMessages[messageIndex];
};

const ExpenseGroupPreviewJournal: FC<Props> = ({
	isOpen,
	onClose,
	selectedExpenseGroup,
	dateRange,
	expenseGroupOptions: passedExpenseGroupOptions,
	existingJournalData,
	isExistingJournal = false,
	journalId,
	onSwitchToJournalEntries,
	hideSaveAsDraft = false,
	configWasSaved = false,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const [journalDate, setJournalDate] = useState(
		dayjs().tz(TIMEZONE).endOf('month')
	);
	const [journalExists, setJournalExists] = useState<boolean>(false);
	const [publishIsLoading, setPublishIsLoading] = useState<boolean>(false);
	const [draftIsLoading, setDraftIsLoading] = useState<boolean>(false);
	const [showDuplicateErrorModal, setShowDuplicateErrorModal] = useState<boolean>(false);
	const [duplicateErrorData, setDuplicateErrorData] = useState<any>(null);

	// const { data: expenseGroups } = useSelector(
	// 	(state: any) => state.expenseGroups
	// );

	// Redux state for expense journal preview
	const { previewEntriesData, previewEntriesLoading } = useSelector(
		(state: any) => state.expenseJournal
	);

	// Get temporary config and single account mode from Redux
	const { temporaryConfig } = useSelector(
		(state: any) => state.expenseGroupAllocation
	);

	// Get one-off configurations for One-Off mode
	const { oneOffConfigurations } = useSelector((state: any) => state.oneOff);

	// Get expense groups from Redux to access the selected expense group's filters
	const { data: expenseGroups } = useSelector(
		(state: any) => state.expenseGroups
	);

	// Check if One-Off mode is ON
	const isOneOffMode = selectedExpenseGroup === 'all';

	// Get chart of accounts to lookup account names
	const { data: chartOfAccounts } = useSelector(
		(state: any) => state.chartOfAccounts
	);

	// Get sort config from Redux (same as SearchAndFilter uses)


	// Get the selected expense group's configured accounts, customers, classes, locations
	// This matches what's used in the transaction filters when an expense group is selected
	// Memoize to prevent unnecessary re-renders


	// Permission checks
	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	const isExpenseJournalPermissionEdit = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Expense Journal',
			permission: ['edit'],
		}
	);

	const isExpenseJournalPermissionAdd = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Expense Journal',
			permission: ['add'],
		}
	);

	// Calculate the last date of the month from dateRange in the correct timezone
	const getLastDateOfMonth = () => {
		if (dateRange && dateRange[1]) {
			// Use the end date of the range and get the last day of that month in the correct timezone
			return dayjs(dateRange[1]).endOf('month');
		}
		// Fallback to current date if no date range
		return dayjs().endOf('month');
	};

	// Function to generate notes based on date range, expense group, and allocations
	const generateNotes = (dateRangeStr: string, expenseGroup: string) => {
		// Get expense group label
		const expenseGroupOptionsToUse = passedExpenseGroupOptions || [];
		const expenseGroupLabel =
			expenseGroupOptionsToUse.find((e) => e.value === expenseGroup)?.label ||
			expenseGroup;

		return `${dateRangeStr} ${expenseGroupLabel}`;
	};

	// Generate default notes with current date range or passed date range
	const getDateRangeString = () => {
		if (dateRange && dateRange[0] && dateRange[1]) {
			return `${dateRange[0].format('MM/DD/YYYY')} - ${dateRange[1].format(
				'MM/DD/YYYY'
			)}`;
		}
		return '';
	};

	const defaultDateRange = getDateRangeString();

	// Journal data state similar to JournalEntries PreviewJournal
	const [journalData, setJournalData] = useState<any>({
		journalDate: getLastDateOfMonth(),
		notes: generateNotes(defaultDateRange, selectedExpenseGroup),
		qboJournalNumber: null,
	});

	const [notes, setNotes] = useState(
		generateNotes(defaultDateRange, selectedExpenseGroup)
	);

	const [showScrollToBottom, setShowScrollToBottom] = useState(false);
	const [initializedJournalId, setInitializedJournalId] = useState<
		string | null
	>(null);

	const [attachmentData, setAttachmentData] = useState<string>('');
	const [attachmentFileName, setAttachmentFileName] = useState<string>('');

	// Get company setting for summarized view
	const companySummarizedView = useSelector(
		(state: any) =>
			state.companies?.currentCompany?.isSummarizedJournalView ?? false
	);

	// Handle existing journal data initialization - only run once per journal
	useEffect(() => {
		if (
			isOpen &&
			isExistingJournal &&
			existingJournalData &&
			existingJournalData.id !== initializedJournalId
		) {
			setJournalData({
				journalDate: existingJournalData.date
					? dayjs.tz(existingJournalData.date, TIMEZONE)
					: getLastDateOfMonth(),
				journalNumber: existingJournalData.qboJournalNo || 1,
				notes:
					existingJournalData.notes ||
					generateNotes(defaultDateRange, selectedExpenseGroup),
				qboJournalNumber: existingJournalData.qboJournalNo || null,
			});
			setNotes(
				existingJournalData.notes ||
				generateNotes(defaultDateRange, selectedExpenseGroup)
			);
			setInitializedJournalId(existingJournalData.id);
		}
	}, [isOpen, isExistingJournal, existingJournalData, initializedJournalId]);

	// Reset initialized journal ID when modal closes
	useEffect(() => {
		if (!isOpen) {
			setInitializedJournalId(null);
		}
	}, [isOpen]);

	// If the preview API returns journalMetadata (when called from preview svg),
	// use it to initialize the journal fields so the UI shows the correct date
	// and notes instead of defaulting to last day of month.
	useEffect(() => {
		if (
			isOpen &&
			isExistingJournal &&
			previewEntriesData &&
			previewEntriesData.journalMetadata
		) {
			console.log(previewEntriesData, 'previewentriesdata');
			console.log(
				previewEntriesData.journalMetadata.date,
				'previewentriesdata.journalMetadata'
			);
			const meta = previewEntriesData.journalMetadata;
			if (meta.id !== initializedJournalId) {
				setJournalData({
					journalDate: meta.date
						? dayjs.tz(meta.date, TIMEZONE)
						: getLastDateOfMonth(),
					journalNumber: meta.qboJournalNo || 1,
					notes:
						meta.notes || generateNotes(defaultDateRange, selectedExpenseGroup),
				});
				if(meta.status === 1) {
					setJournalData((prevData: any) => ({
						...prevData,
						qboJournalNumber: meta.qboJournalNo || null,
					}));
				}
				setNotes(
					meta.notes || generateNotes(defaultDateRange, selectedExpenseGroup)
				);
				// Note: isSummarizedView is now read from company settings, not from metadata
				// The metadata value is used by the backend, but the UI uses the company setting
				setInitializedJournalId(meta.id);
				setJournalExists(true);
			}
		}
	}, [
		isOpen,
		isExistingJournal,
		previewEntriesData,
		initializedJournalId,
		selectedExpenseGroup,
	]);

	// Helper function to find account name by ID from chart of accounts
	const getAccountNameById = (accountId: string): string => {
		if (!accountId || !chartOfAccounts) return '';

		// Recursive function to search through tree structure
		const findAccount = (items: any[]): string | null => {
			for (const item of items || []) {
				if (item.Id === accountId) {
					return item.Name || item.DisplayName || item.FullyQualifiedName || item.title || '';
				}
				if (item.children && item.children.length > 0) {
					const found = findAccount(item.children);
					if (found) return found;
				}
			}
			return null;
		};

		return findAccount(chartOfAccounts) || '';
	};

	// Function to fetch expense journal preview entries
	const fetchExpenseJournalPreview = async () => {
		// Check if journalId is provided - if yes, determine route based on journal mode
		if (journalId) {
			// Check if this is a one-off journal (from existingJournalData.mode)
			const isOneOffJournal = existingJournalData?.mode === 'oneOff';

			if (isOneOffJournal) {
				// Use one-off preview route for one-off journals
				const params = {
					companyId: localStorage.getItem('companyId'),
					journalId: journalId,
					isSummarizedJournalView: companySummarizedView,
				};

				try {
					const result = await dispatch(
						previewOneOffExpenseJournalEntriesAction(params)
					);
					console.log('API response result:', result);
					return;
				} catch (error) {
					console.error('Error fetching one-off expense journal preview:', error);
					toastText('Failed to fetch expense journal preview data', 'error');
					return;
				}
			} else {
				// Use normal preview route for regular journals
				const params = {
					companyId: localStorage.getItem('companyId'),
					journalId: journalId,
					isSummarizedJournalView: companySummarizedView,
				};

				try {
					const result = await dispatch(
						getExpenseJournalPreviewEntriesAction(params)
					);
					console.log('API response result:', result);
					return;
				} catch (error) {
					console.error('Error fetching expense journal preview:', error);
					toastText('Failed to fetch expense journal preview data', 'error');
					return;
				}
			}
		}

		// When journalId is not provided, fromDate and toDate are ALWAYS required
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			toastText('Please select a valid date range', 'error');
			return;
		}

		// One-Off mode: use one-off preview route
		if (isOneOffMode) {
			if (!oneOffConfigurations) {
				toastText('One-off configuration is required', 'error');
				return;
			}

			const params: any = {
				companyId: localStorage.getItem('companyId'),
				fromDate: dateRange[0].format('YYYY-MM-DD'),
				toDate: dateRange[1].format('YYYY-MM-DD'),
				oneOffConfigurations: oneOffConfigurations,
				page: 1,
				limit: 10,
				isSummarizedJournalView: companySummarizedView,
			};

			// Add optional fields for account replacement if applicable
			if (oneOffConfigurations.isSingleAccountAllocation && oneOffConfigurations.configAccount) {
				params.isSingleAccountAllocation = true;
				params.configAccount = oneOffConfigurations.configAccount;
			}

			try {
				const result = await dispatch(
					previewOneOffExpenseJournalEntriesAction(params)
				);
				console.log('API response result:', result);
				return;
			} catch (error) {
				console.error('Error fetching one-off expense journal preview:', error);
				toastText('Failed to fetch expense journal preview data', 'error');
				return;
			}
		}

		// Normal mode: use existing route with expense group
		// Check if temporary config exists
		const hasTemporaryConfig =
			temporaryConfig &&
			temporaryConfig.expenseGroupId === selectedExpenseGroup;

		// Base payload - always includes required fields
		const params: any = {
			companyId: localStorage.getItem('companyId'),
			expenseGroupId: selectedExpenseGroup,
			fromDate: dateRange[0].format('YYYY-MM-DD'),
			toDate: dateRange[1].format('YYYY-MM-DD'),
			isSummarizedJournalView: companySummarizedView,
		};

		// Scenario: Has temporary config (tempFlag = true)
		if (hasTemporaryConfig) {
			// Get the selected expense group data to access accounts
			const selectedExpenseGroupData = expenseGroups?.find(
				(group: any) => group.id === selectedExpenseGroup
			);

			// Get isSingleAccountAllocation from temporaryConfig (payload source)
			const isSingleAccountAllocation = temporaryConfig.singleAccountMode ?? false;

			// Build tempConfigAllocations array
			const tempConfigAllocations = (temporaryConfig.items || []).map((item: any) => ({
				percentage: Number(item.percentage) || 0,
				customerId: item.customerId || null,
				customerName: item.customerName || null,
				classId: item.classId || null,
				className: item.className || null,
				locationId: item.locationId || null,
				locationName: item.locationName || null,
			}));

			// Validate tempConfigAllocations
			if (tempConfigAllocations.length === 0) {
				toastText('Temporary allocation configurations are required', 'error');
				return;
			}

			// Determine tempAccounts and configAccount based on switch state
			let tempAccounts: string[] = [];

			if (isSingleAccountAllocation) {
				// Switch is ON:
				// - configAccount = single selected account from modal (temporaryConfig.configAccounts[0])
				// - tempAccounts = expenseGroup.accounts (can be empty - backend handles it)
				const selectedAccounts = temporaryConfig.configAccounts || [];
				if (selectedAccounts.length === 0) {
					toastText('Single account selection is required', 'error');
					return;
				}

				// Get expense group accounts for tempAccounts
				// Can be empty array - backend will handle it
				tempAccounts = selectedExpenseGroupData?.accounts || [];

				// configAccount is the single selected account from modal
				const configAccountId = selectedAccounts[0];
				const configAccountName = getAccountNameById(configAccountId);

				if (!configAccountName) {
					console.error('Unable to find account name for configuration account', 'error');
					return;
				}

				params.configAccount = {
					name: configAccountName,
					value: configAccountId,
				};
			} else {
				// Switch is OFF:
				// - tempAccounts = selected accounts from modal multiple dropdown (temporaryConfig.configAccounts)
				// - No configAccount
				// Allow empty array - backend will handle it
				tempAccounts = temporaryConfig.configAccounts || [];
			}

			// Add temporary mode fields
			params.tempFlag = true;
			params.tempAccounts = tempAccounts;
			params.tempConfigAllocations = tempConfigAllocations;
			params.isSingleAccountAllocation = isSingleAccountAllocation;
		}
		// Scenario: No temporary config (tempFlag = false, normal mode)
		// When tempFlag = false, backend uses expenseGroup.isSingleAccountAllocation from database
		// We don't send isSingleAccountAllocation in payload (backend ignores it anyway)
		// Just send the base required fields

		try {
			const result = await dispatch(
				getExpenseJournalPreviewEntriesAction(params)
			);
			console.log('API response result:', result);
		} catch (error) {
			console.error('Error fetching expense journal preview:', error);
			toastText('Failed to fetch expense journal preview data', 'error');
		}
	};

	// Function to fetch latest journal number
	const fetchLatestJournalNumber = async () => {
		try {
			const latestJournalNo: any = await getApi('/journal/latest-no', {
				companyId: localStorage.getItem('companyId'),
			});

			let latestNo = 1; // Default to 1 if no response
			if (latestJournalNo.data.data) {
				latestNo = latestJournalNo.data.data.latestNo;
			}

			return latestNo;
		} catch (err) {
			console.error('Error fetching latest journal number:', err);
			return 1; // Return default if API fails
		}
	};
	const createTransactionsCsv = useCallback(async () => {
		if (
			!dateRange ||
			!dateRange[0] ||
			!dateRange[1] ||
			!selectedExpenseGroup
		) {
			return;
		}

		try {
			// Revoke previous blob URL if it exists
			setAttachmentData((prevAttachmentData) => {
				if (prevAttachmentData) {
					URL.revokeObjectURL(prevAttachmentData);
				}
				return prevAttachmentData; // Keep the old value while loading
			});
			setAttachmentFileName(''); // Clear filename while loading

			// Get the selected expense group data to extract accounts, customers, classes, locations
			const selectedExpenseGroupData = expenseGroups?.find(
				(group: any) => group.id === selectedExpenseGroup
			);

			const params: any = {
				companyId: localStorage.getItem('companyId'),
				fromDate: dateRange[0].format('YYYY-MM-DD'),
				toDate: dateRange[1].format('YYYY-MM-DD'),
			};

			// Add accountIds if available
			if (
				selectedExpenseGroupData?.accounts &&
				selectedExpenseGroupData.accounts.length > 0
			) {
				params.accountIds = selectedExpenseGroupData.accounts;
			}

			// Add customerIds if available
			if (
				selectedExpenseGroupData?.customers &&
				selectedExpenseGroupData.customers.length > 0
			) {
				params.customerIds = selectedExpenseGroupData.customers;
			}

			// Add classIds if available
			if (
				selectedExpenseGroupData?.classes &&
				selectedExpenseGroupData.classes.length > 0
			) {
				params.classIds = selectedExpenseGroupData.classes;
			}

			// Add locationIds if available
			if (
				selectedExpenseGroupData?.locations &&
				selectedExpenseGroupData.locations.length > 0
			) {
				params.locationIds = selectedExpenseGroupData.locations;
			}

			// Add vendorIds if available
			if (
				selectedExpenseGroupData?.vendors &&
				selectedExpenseGroupData.vendors.length > 0
			) {
				params.vendorIds = selectedExpenseGroupData.vendors;
			}
			if (isOneOffMode) {
				if (oneOffConfigurations) {
					params.accountIds = oneOffConfigurations.accountIds;
					params.customerIds = oneOffConfigurations.customerIds;
					params.classIds = oneOffConfigurations.classIds;
					params.locationIds = oneOffConfigurations.locationIds;
					params.vendorIds = oneOffConfigurations.vendorIds;
				}
			}

			// Add default sort parameters
			params.sortBy = 'txnDate';
			params.sortOrder = 'desc';

			const response: any = await getApiCSV(
				'/transactions/export-csv',
				params
			);

			const blob = new Blob([response.data], {
				type: 'text/csv',
			});
			const blobUrl = URL.createObjectURL(blob);

			// Generate filename in the same format as SearchAndFilter
			const fileName = moment
				.tz(new Date(), TIMEZONE)
				.format('MMDDYYYYhhmmss');
			const fullFileName = `Transactions_${fileName}.csv`;

			setAttachmentData(blobUrl);
			setAttachmentFileName(fullFileName);
		} catch (err) {
			console.error('Error creating transactions CSV attachment:', err);
			// Don't show error toast as this is a background operation
		}
	}, [selectedExpenseGroup, dateRange, expenseGroups]);

	const modalContentRef = useRef<HTMLDivElement | null>(null);
	const currentLoadingMessage = useLoadingMessages();

	// Mock configurations (similar to PreviewJournal)
	const configurationsData = { decimalToFixedAmount: 2 };

	// Transform API data to JournalRow format
	const dataSource = useMemo<JournalRow[]>(() => {
		if (!previewEntriesData) {
			console.log('No previewEntriesData available');
			return [];
		}

		// Handle different possible response structures
		let entriesArray = previewEntriesData;
		if (previewEntriesData.data && Array.isArray(previewEntriesData.data)) {
			entriesArray = previewEntriesData.data;
		} else if (
			previewEntriesData.entries &&
			Array.isArray(previewEntriesData.entries)
		) {
			entriesArray = previewEntriesData.entries;
		} else if (!Array.isArray(previewEntriesData)) {
			return [];
		}

		return entriesArray.map((item: any, index: number) => ({
			key: item.key || item.id?.toString() || index.toString(),
			id: item.id || index,
			accountId: item.accountId || '',
			account: item.account || item.accountName || '',
			type: item.type || 'Debit',
			debit:
				item.debit !== undefined && item.debit !== null && item.debit !== ''
					? item.debit
					: item.debitAmount !== undefined &&
						item.debitAmount !== null &&
						item.debitAmount !== ''
						? item.debitAmount
						: '',
			credit:
				item.credit !== undefined && item.credit !== null && item.credit !== ''
					? item.credit
					: item.creditAmount !== undefined &&
						item.creditAmount !== null &&
						item.creditAmount !== ''
						? item.creditAmount
						: '',
			description: item.description || null,
			customerId: item.customerId || null,
			customer: item.customer || item.customerName || null,
			classId: item.classId || null,
			class: item.class || item.className || null,
			locationId: item.locationId || null,
			location: item.location || item.locationName || null,
		}));
	}, [previewEntriesData]);

	// Scroll functionality (mimicking PreviewJournal)
	const handleScroll = () => {
		if (modalContentRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = modalContentRef.current;
			const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;
			setShowScrollToBottom(scrollTop > 200 && !isNearBottom);
		}
	};

	const scrollToBottom = () => {
		if (modalContentRef.current) {
			modalContentRef.current.scrollTo({
				top: modalContentRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}
	};

	useEffect(() => {
		const modalContent = modalContentRef.current;
		if (modalContent) {
			modalContent.addEventListener('scroll', handleScroll);
			return () => {
				modalContent.removeEventListener('scroll', handleScroll);
			};
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && selectedExpenseGroup) {
			const hasMetadata =
				previewEntriesData && previewEntriesData.journalMetadata;
			if (!isExistingJournal && !hasMetadata) {
				// For new journal without metadata, fetch data and set last date of month
				// fetchExpenseJournalPreview will handle dateRange validation internally
				fetchExpenseJournalPreview();
				const lastDateOfMonth = getLastDateOfMonth();
				setJournalDate(lastDateOfMonth);
				setJournalData((prevData: any) => ({
					...prevData,
					journalDate: lastDateOfMonth,
				}));
			} else if (!isExistingJournal && hasMetadata) {
				fetchExpenseJournalPreview();
			}
		}
	}, [isOpen, selectedExpenseGroup, dateRange, isExistingJournal])
	useEffect(() => {
		if (
			isOpen &&
			selectedExpenseGroup &&
			dateRange &&
			dateRange[0] &&
			dateRange[1]
		) {
			createTransactionsCsv();
		}
		// Cleanup: revoke blob URL when component unmounts or modal closes
		return () => {
			setAttachmentData((prevAttachmentData) => {
				if (prevAttachmentData) {
					URL.revokeObjectURL(prevAttachmentData);
				}
				return '';
			});
			setAttachmentFileName('');
		};
	}, [isOpen, createTransactionsCsv]);

	const dateChangeHandler = (date: any) => {
		const newDate = date ? dayjs.tz(date, TIMEZONE) : dayjs().tz(TIMEZONE);
		setJournalDate(newDate);
		setJournalData((prevData: any) => ({
			...prevData,
			journalDate: newDate,
		}));
	};

	const notesHandler = (value: any) => {
		setNotes(value.target.value);
		setJournalData((prevData: any) => {
			return {
				...prevData,
				notes: value.target.value,
			};
		});
	};

	// Handle submit function similar to existing PreviewJournal
	const handleSubmit = async (status: number) => {
		try {
			//Check for Notes length limit
			if (journalData?.notes?.length > 4000) {
				toastText('The Notes field must not exceed 4000 characters', 'error');
				return;
			}
			if (dataSource && dataSource.length > 0) {
				if (status === EJournalStatus.PUBLISHED) {
					setPublishIsLoading(true);
				} else if (status === EJournalStatus.DRAFT) {
					setDraftIsLoading(true);
				}

				// Validate date range is required
				if (!dateRange || !dateRange[0] || !dateRange[1]) {
					toastText('Please select a valid date range', 'error');
					setPublishIsLoading(false);
					setDraftIsLoading(false);
					return;
				}

				// Create expense journal entry
				const dateRangeString = `${dateRange[0].format('YYYY-MM-DD')} - ${dateRange[1].format(
					'YYYY-MM-DD'
				)}`;

				// Calculate total amount from journal entries
				const totalAmount = dataSource.reduce((total, entry) => {
					// Skip the Total row
					if (entry.account === 'Total') {
						return total;
					}
					const debit = parseFloat(entry.debit?.toString() || '0');
					return total + debit;
				}, 0);
				console.log(journalData, 'journalData');
				const latestJournalNo = await fetchLatestJournalNumber();

				// One-Off mode: use one-off create/save-draft routes
				if (isOneOffMode) {
					// Check if this is an update (id provided)
					const isUpdate = journalId || existingJournalData?.id;

					if (!isUpdate && !oneOffConfigurations) {
						toastText('One-off configuration is required', 'error');
						setPublishIsLoading(false);
						setDraftIsLoading(false);
						return;
					}

					const payload: any = {
						companyId: localStorage.getItem('companyId'),
						date: journalData?.journalDate
							? dayjs(journalData?.journalDate).format('YYYY-MM-DD')
							: undefined,
						dateRange: dateRangeString,
						qboJournalNo: String(
							journalData?.qboJournalNumber || latestJournalNo
						),
						status: status,
						notes: journalData?.notes || '',
						amount: totalAmount.toString(),
						isSummarizedJournalView: companySummarizedView,
					};

					// Add id field if this is an existing journal (for update instead of create)
					if (isUpdate) {
						payload.id = journalId || existingJournalData.id;
						// When updating, don't send oneOffConfigurations, isSingleAccountAllocation, or configAccount
						// Backend will retrieve these from the database
					} else {
						// When creating new journal, send oneOffConfigurations
						payload.oneOffConfigurations = oneOffConfigurations;

						// Add optional fields for account replacement if applicable
						if (oneOffConfigurations.isSingleAccountAllocation && oneOffConfigurations.configAccount) {
							payload.isSingleAccountAllocation = true;
							payload.configAccount = oneOffConfigurations.configAccount;
						}
					}

					// Determine endpoint based on status
					const endpoint =
						status === EJournalStatus.DRAFT
							? '/expense-journal/one-off-save-draft'
							: '/expense-journal/one-off-create';

					await postApi(endpoint, payload);

					toastText(
						`Expense Journal ${journalExists ? 'edited' : 'created'
						} successfully`,
						'success'
					);
					setPublishIsLoading(false);
					setDraftIsLoading(false);

					// Refresh the journal entries list to show the latest data
					dispatch(
						getExpenseJournalListAction({
							page: 1,
							limit: 10,
							search: '',
							status: '',
							sort: 'date',
							sortType: 'desc',
							year: new Date().getFullYear(),
						})
					);

					// Close the modal and switch to Journal Entries tab
					onClose();
					if (onSwitchToJournalEntries) {
						onSwitchToJournalEntries();
					}
					return;
				}

				// Normal mode: use existing routes
				// Get the selected expense group data
				const selectedExpenseGroupData = expenseGroups?.find(
					(group: any) => group.id === selectedExpenseGroup
				);

				// Check if temporaryConfig exists
				const hasTemporaryConfig =
					temporaryConfig &&
					temporaryConfig.expenseGroupId === selectedExpenseGroup;

				// Base payload - always includes required fields
				const payload: any = {
					companyId: localStorage.getItem('companyId'),
					expenseGroupId: selectedExpenseGroup,
					dateRange: dateRangeString,
					// Send date as a plain date string (YYYY-MM-DD). Journal entries
					// elsewhere in the app expect date-only strings; sending a
					// timezone-aware value caused the backend to treat it differently
					// and the UI wouldn't update. Normalize here to avoid that.
					date: journalData?.journalDate
						? dayjs(journalData?.journalDate).format('YYYY-MM-DD')
						: undefined,
					qboJournalNo: String(
						journalData?.qboJournalNumber || latestJournalNo
					),
					status: status,
					notes: journalData?.notes || '',
					amount: totalAmount.toString(),
					createdById: localStorage.getItem('userId') || '',
					isSummarizedJournalView: companySummarizedView,
				};

				// Add id field if this is an existing journal (for update instead of create)
				if (journalId || existingJournalData?.id) {
					payload.id = journalId || existingJournalData.id;
				}

				// Add temporary mode fields if tempConfig exists
				if (hasTemporaryConfig) {
					// Get isSingleAccountAllocation from temporaryConfig (payload source)
					const isSingleAccountAllocation = temporaryConfig.singleAccountMode ?? false;

					// Build tempConfigAllocations array
					const tempConfigAllocations = (temporaryConfig.items || []).map((item: any) => ({
						percentage: Number(item.percentage) || 0,
						customerId: item.customerId || null,
						customerName: item.customerName || null,
						classId: item.classId || null,
						className: item.className || null,
						locationId: item.locationId || null,
						locationName: item.locationName || null,
					}));

					// Validate tempConfigAllocations
					if (tempConfigAllocations.length === 0) {
						toastText('Temporary allocation configurations are required', 'error');
						setPublishIsLoading(false);
						setDraftIsLoading(false);
						return;
					}

					// Determine tempAccounts and configAccount based on switch state
					let tempAccounts: string[] = [];

					if (isSingleAccountAllocation) {
						// Switch is ON:
						// - configAccount = single selected account from modal (temporaryConfig.configAccounts[0])
						// - tempAccounts = expenseGroup.accounts (can be empty - backend handles it)
						const selectedAccounts = temporaryConfig.configAccounts || [];
						if (selectedAccounts.length === 0) {
							toastText('Single account selection is required', 'error');
							setPublishIsLoading(false);
							setDraftIsLoading(false);
							return;
						}

						// Get expense group accounts for tempAccounts
						tempAccounts = selectedExpenseGroupData?.accounts || [];

						// configAccount is the single selected account from modal
						const configAccountId = selectedAccounts[0];
						const configAccountName = getAccountNameById(configAccountId);

						if (!configAccountName) {
							toastText('Unable to find account name for configuration account', 'error');
							setPublishIsLoading(false);
							setDraftIsLoading(false);
							return;
						}

						payload.configAccount = {
							name: configAccountName,
							value: configAccountId,
						};
					} else {
						// Switch is OFF:
						// - tempAccounts = selected accounts from modal multiple dropdown (temporaryConfig.configAccounts)
						// - No configAccount
						tempAccounts = temporaryConfig.configAccounts || [];
					}

					// Add temporary mode fields
					payload.tempFlag = true;
					payload.tempAccounts = tempAccounts;
					payload.tempConfigAllocations = tempConfigAllocations;
					payload.isSingleAccountAllocation = isSingleAccountAllocation;
				}
				// When tempFlag = false (no temporaryConfig), no additional fields needed
				// Backend will use expenseGroup.accounts and database allocations

				// Always include flag indicating if config was saved when user clicked "Yes"
				payload.configWasSaved = configWasSaved || false;

				const endpoint =
					status === EJournalStatus.DRAFT
						? '/expense-journal/save-draft'
						: '/expense-journal/create';
				await postApi(endpoint, payload);

				toastText(
					`Expense Journal ${journalExists ? 'edited' : 'created'
					} successfully`,
					'success'
				);
				setPublishIsLoading(false);
				setDraftIsLoading(false);

				// Refresh the journal entries list to show the latest data
				dispatch(
					getExpenseJournalListAction({
						page: 1,
						limit: 10,
						search: '',
						status: '',
						sort: 'date',
						sortType: 'desc',
						year: new Date().getFullYear(),
					})
				);

				// Close the modal and switch to Journal Entries tab
				onClose();
				if (onSwitchToJournalEntries) {
					onSwitchToJournalEntries();
				}
			}
		} catch (err: any) {
			setPublishIsLoading(false);
			setDraftIsLoading(false);

			// Check for duplicate journal error with attemptDetails
			if (
				err?.response?.data?.error?.additionalInfo?.attemptDetails &&
				Array.isArray(err?.response?.data?.error?.additionalInfo?.attemptDetails) &&
				err?.response?.data?.error?.additionalInfo?.attemptDetails.length > 0
			) {
				setDuplicateErrorData({
					attempts: err?.response?.data?.error?.additionalInfo?.attempts || err?.response?.data?.error?.additionalInfo?.attemptDetails.length,
					attemptDetails: err?.response?.data?.error?.additionalInfo?.attemptDetails,
				});
				setShowDuplicateErrorModal(true);
			} else if (err?.response?.status === 504) {
				toastText(
					'The request timed out. This may be due to a large journal entry exceeding supported limits.',
					'error'
				);
			} else if (!err?.response) {
				toastText('Something went wrong', 'error');
			} else {
				toastText(
					err?.response?.data?.message ||
					'An unexpected error occurred while submitting the journal',
					'error'
				);
			}
		}
	};

	// Handle close - temporary config persists until page refresh
	const handleClose = async () => {
		onClose();
	};

	return (
		<>
			<Modal
				open={isOpen}
				onCancel={handleClose}
				className="preview-container"
				closable={false}
				style={{ borderRadius: 0 }}
				footer={[]}
				width="100%"
			>
				<div>
					<Row
						className={
							styles?.['preview-container__top'] || 'preview-container__top'
						}
					>
						<div
							className={
								styles?.['preview-container__top-title'] ||
								'preview-container__top-title'
							}
						>
							<b>Journal Preview</b>
						</div>
						<div
							className={
								styles?.['preview-container__top-close'] ||
								'preview-container__top-close'
							}
							onClick={handleClose}
							style={{ cursor: 'pointer' }}
						>
							<CloseSvg />
						</div>
					</Row>

					<div
						className={
							styles?.['preview-container__content'] ||
							'preview-container__content'
						}
						ref={modalContentRef}
						style={{
							maxHeight: 'calc(100vh - 100px)',
							overflowY: 'auto',
							position: 'relative',
						}}
					>
						{/* Header Section */}
						<div
							className={
								styles?.['preview-container__content--header'] ||
								'preview-container__content--header'
							}
							style={{ marginBottom: '16px' }}
						>
							<p
								className={
									styles?.['preview-container__content--header-title'] ||
									'preview-container__content--header-title'
								}
								style={{ fontSize: '14px', fontWeight: 'bold' }}
							>
								Journal Entry{' '}
								{journalData?.qboJournalNumber
									? `#${journalData.qboJournalNumber}`
									: ''}
							</p>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '16px',
									flexWrap: 'wrap',
								}}
							>
								<div
									className={
										styles?.['preview-container__content--header-date'] ||
										'preview-container__content--header-date'
									}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										fontSize: '12px',
									}}
								>
									Journal Date:{' '}
									<DatePicker
										value={journalData?.journalDate || journalDate}
										onChange={dateChangeHandler}
										format={'MM/DD/YYYY'}
										allowClear={false}
										size="large"
										disabled={
											(journalExists && !isExpenseJournalPermissionEdit) ||
											(!journalExists && !isExpenseJournalPermissionAdd)
										}
									/>
								</div>
							</div>
						</div>

						{/* Table Section */}
						<div
							className={
								styles?.['preview-container__content--table'] ||
								'preview-container__content--table'
							}
						>
							<Table
								dataSource={dataSource}
								sticky
								pagination={false}
								style={{ borderRadius: 0 }}
								loading={{
									spinning: previewEntriesLoading,
									indicator: (
										<div
											style={{
												textAlign: 'center',
												padding: '50px 0',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
												minWidth: '200px',
												marginLeft: '-100px',
												marginTop: '12px',
											}}
										>
											<Spin
												indicator={
													<LoadingOutlined
														spin
														style={{ color: '#485949', fontSize: '30px' }}
													/>
												}
												className="custom-spinner"
											/>
											<div
												style={{
													fontSize: '16px',
													color: '#485949',
													fontWeight: '600',
													minHeight: '20px',
													transition: 'opacity 0.3s ease-in-out',
													whiteSpace: 'nowrap',
													textAlign: 'center',
													paddingTop: '40px',
													marginLeft: '14px',
												}}
											>
												{currentLoadingMessage}
											</div>
										</div>
									),
								}}
								rowKey={(record: any) => record.key}
								rowClassName={(record: any) => {
									if (record.account === 'Total') {
										return 'journal-total-row';
									}
									return '';
								}}
							>
								<Column
									title={() => <p style={{ fontSize: '12px' }}>#</p>}
									dataIndex="id"
									key="id"
									className="bg-white journal-dashed-border"
									width={60}
									render={(value: string | number, record: any) => (
										<span className={styles?.['dynamic-table__hash']}>
											{record.account === 'Total' ? '' : value}
										</span>
									)}
								/>

								<Column
									title={() => <p style={{ fontSize: '12px' }}>Account</p>}
									dataIndex="account"
									key="account"
									className="bg-white journal-dashed-border"
									render={(value: string | number) => (
										<div className={styles?.['dynamic-table__account']}>
											{value}
										</div>
									)}
								/>

								<Column
									title={() => <p style={{ fontSize: '12px' }}>Debit(+)</p>}
									dataIndex="debit"
									key="debit"
									className="bg-white journal-dashed-border"
									align="right"
									width={120}
									render={(value: string | number, record: any) => (
										<NumericFormat
											prefix={record.account === 'Total' ? '$' : ''}
											value={value}
											thousandSeparator=","
											decimalScale={configurationsData?.decimalToFixedAmount || 2}
											fixedDecimalScale={true}
											displayType="text"
											renderText={(value) => (
												<p
													className={styles?.['dynamic-table-total']}
													style={{
														textAlign: 'right',
														fontSize: '12px',
													}}
												>
													{value ?? ''}
												</p>
											)}
										/>
									)}
								/>

								<Column
									title={() => <p style={{ fontSize: '12px' }}>Credit(-)</p>}
									dataIndex="credit"
									key="credit"
									className="bg-white journal-dashed-border credit-font"
									align="right"
									width={120}
									render={(value: string | number, record: any) => (
										<NumericFormat
											prefix={record.account === 'Total' ? '$' : ''}
											value={value}
											thousandSeparator=","
											decimalScale={configurationsData?.decimalToFixedAmount || 2}
											fixedDecimalScale={true}
											displayType="text"
											renderText={(value) => (
												<p
													className={styles?.['dynamic-table-total']}
													style={{
														textAlign: 'right',
														fontSize: '12px',
													}}
												>
													{value ?? ''}
												</p>
											)}
										/>
									)}
								/>
								<Column
									title={() => <p style={{ fontSize: '12px' }}>Description</p>}
									dataIndex="description"
									key="description"
									className="bg-white journal-dashed-border"
									width={300}
									render={(value: string | number) => (
										<span className={styles?.['dynamic-table__description']}>
											<Ellipse
												message={`${value || ''}`}
												maxLength={60}
												isTooltip={true}
												tooltipMessage={`${value || ''}`}
											/>
										</span>
									)}
								/>

								<Column
									title={() => <p style={{ fontSize: '12px' }}>Customer Name</p>}
									dataIndex="customer"
									key="customer"
									className="bg-white journal-dashed-border"
									render={(value: string | number) => (
										<span className={styles?.['dynamic-table__customer']}>
											{value || ''}
										</span>
									)}
								/>

								<Column
									title={() => <p style={{ fontSize: '12px' }}>Class</p>}
									dataIndex="class"
									key="class"
									className="bg-white journal-dashed-border"
									render={(value: string | number) => (
										<span className={styles?.['dynamic-table__class']}>
											{value || ''}
										</span>
									)}
								/>

								{/* Added Location Column */}
								<Column
									title={() => <p style={{ fontSize: '12px' }}>Location</p>}
									dataIndex="location"
									key="location"
									className="bg-white journal-dashed-border"
									render={(value: string | number) => (
										<span className={styles?.['dynamic-table__location']}>
											{value || ''}
										</span>
									)}
								/>
							</Table>
						</div>

						{/* Footer Section */}
						<div
							className={
								styles?.['preview-container__content--footer'] ||
								'preview-container__content--footer'
							}
							style={{ marginTop: '20px' }}
						>
							<Row gutter={32}>
								<Col xs={16} sm={16} md={16} lg={16}>
									<div
										className={
											styles?.['preview-container__content--footer-notes'] ||
											'preview-container__content--footer-notes'
										}
									>
										<label style={{ fontSize: '12px' }} htmlFor="my-textarea">
											Notes:
										</label>
										<TextArea
											rows={3}
											className={
												styles?.['preview-container__content--footer-textarea']
											}
											value={journalData?.notes || notes}
											onChange={notesHandler}
											disabled={
												(journalExists && !isExpenseJournalPermissionEdit) ||
												(!journalExists && !isExpenseJournalPermissionAdd)
											}
										/>
									</div>
								</Col>

								<Col
									xs={8}
									sm={8}
									md={8}
									lg={8}
									className={
										styles?.['preview-container__content--footer-upload']
									}
								>
									{attachmentData && !previewEntriesLoading && (
										<>
											<p style={{ fontSize: '12px' }}>Attachment: </p>
											<a
												href={attachmentData}
												download={attachmentFileName}
												style={{ fontSize: '12px' }}
											>
												{dateRange && dateRange[0] && dateRange[1]
													? `${attachmentFileName}`
													: 'Expense Transactions Report'}
											</a>
										</>
									)}
								</Col>
							</Row>

							{/* Buttons */}
							<div
								className={
									styles?.['preview-container__content--footer-buttons'] ||
									'preview-container__content--footer-buttons'
								}
								style={{
									display: 'flex',
									gap: '12px',
									justifyContent: 'flex-start',
									alignItems: 'flex-start',
									marginTop: '16px',
								}}
							>
								{!(
									(journalExists && !isExpenseJournalPermissionEdit) ||
									(!journalExists && !isExpenseJournalPermissionAdd)
								) ? (
									<>
										<button
											className={`${styles?.[
												'preview-container__content--footer-buttons-button'
											] || 'preview-container__content--footer-buttons-button'
												} ${(previewEntriesLoading ||
													publishIsLoading ||
													draftIsLoading) &&
												'pointer-event-none'
												}`}
											onClick={() => handleSubmit(EJournalStatus.PUBLISHED)}
											disabled={
												previewEntriesLoading ||
												(journalExists && !isExpenseJournalPermissionEdit) ||
												(!journalExists && !isExpenseJournalPermissionAdd)
											}
										>
											{publishIsLoading ? (
												<img src="/assets/gifs/loading-black.gif" height={40} />
											) : (
												'Save & Publish'
											)}
										</button>
										{/* Hide Save as Draft button if hideSaveAsDraft is true */}
										{!hideSaveAsDraft &&
											(!journalExists ||
												(journalExists &&
													journalData.status != EJournalStatus.PUBLISHED)) ? (
											<button
												className={`${styles?.[
													'preview-container__content--footer-buttons-button'
												] || 'preview-container__content--footer-buttons-button'
													} ${(previewEntriesLoading ||
														publishIsLoading ||
														draftIsLoading ||
														(journalExists && !isExpenseJournalPermissionEdit) ||
														(!journalExists && !isExpenseJournalPermissionAdd)) &&
													'pointer-event-none'
													}`}
												onClick={() => handleSubmit(EJournalStatus.DRAFT)}
												disabled={
													previewEntriesLoading ||
													(journalExists && !isExpenseJournalPermissionEdit) ||
													(!journalExists && !isExpenseJournalPermissionAdd)
												}
											>
												{draftIsLoading ? (
													<img src="/assets/gifs/loading-black.gif" height={40} />
												) : (
													'Save as Draft'
												)}
											</button>
										) : null}
									</>
								) : null}
								<button
									className={
										styles?.[
										'preview-container__content--footer-buttons-cancel'
										] || 'preview-container__content--footer-buttons-cancel'
									}
									onClick={handleClose}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>

					{/* Scroll to Bottom Button */}
					{showScrollToBottom && (
						<div
							style={{
								position: 'fixed',
								right: '60px',
								bottom: '100px',
								zIndex: 1000,
							}}
						>
							<button
								onClick={scrollToBottom}
								style={{
									width: '48px',
									height: '48px',
									borderRadius: '50%',
									border: '1px solid #344734',
									padding: 0,
									margin: 0,
									backgroundColor: '#fff',
									color: '#344734',
									cursor: 'pointer',
									boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '32px',
									fontWeight: 'bold',
									transition: 'all 0.3s ease',
									outline: 'none',
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.backgroundColor = '#344734';
									e.currentTarget.style.color = '#fff';
									e.currentTarget.style.transform = 'scale(1.1)';
									e.currentTarget.style.boxShadow =
										'0 6px 16px rgba(52, 71, 52, 0.3)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.backgroundColor = '#fff';
									e.currentTarget.style.color = '#344734';
									e.currentTarget.style.transform = 'scale(1)';
									e.currentTarget.style.boxShadow =
										'0 4px 12px rgba(0, 0, 0, 0.15)';
								}}
								title="Scroll to bottom"
							>
								<DownCircleOutlined />
							</button>
						</div>
					)}
				</div>
			</Modal>
			<DuplicateJournalErrorModal
				isOpen={showDuplicateErrorModal}
				errorData={duplicateErrorData}
				onClose={() => {
					setShowDuplicateErrorModal(false);
					setDuplicateErrorData(null);
				}}
			/>
		</>
	);
};

export default ExpenseGroupPreviewJournal;
